<template>
  <div class="flex flex-col" :style="{ height: height }">
    <!-- Toolbar -->
    <div class="px-4 py-3 flex items-center flex-wrap gap-3" style="border-bottom: 1px solid #E8E9E9;">
      <h2 class="text-xs font-semibold" style="color: #353537;">Assignments</h2>

      <button
        @click="showAssignModal = true"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-white transition-colors"
        style="background-color: #F76734;"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
        Assign
      </button>



      <div v-if="!hidePrograms" class="flex items-center gap-2">
        <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Program</label>
        <div style="width: 140px;">
          <SimpleMultiSelect
            v-model="programFilter"
            :options="programOptions"
            placeholder="All"
            @change="loadAssignments"
          />
        </div>
      </div>

      <span class="text-xs" style="color: #626264;">
        {{ visibleAssignments.length }} assignments<template v-if="onlyGaps"> of {{ assignments.length }}</template>
      </span>

      <!-- View toggle -->
      <div class="ml-auto flex rounded-md overflow-hidden" style="border: 1px solid #E8E9E9;">
        <button
          @click="viewMode = 'rules'"
          class="px-2.5 py-1 text-xs transition-colors"
          :style="viewMode === 'rules' ? { backgroundColor: '#353537', color: '#fff' } : { color: '#626264' }"
        >By Rule</button>
        <button
          @click="viewMode = 'member'"
          class="px-2.5 py-1 text-xs transition-colors"
          :style="viewMode === 'member' ? { backgroundColor: '#353537', color: '#fff' } : { color: '#626264' }"
          style="border-left: 1px solid #E8E9E9;"
        >By Member</button>
        <button
          @click="viewMode = 'entity'"
          class="px-2.5 py-1 text-xs transition-colors"
          :style="viewMode === 'entity' ? { backgroundColor: '#353537', color: '#fff' } : { color: '#626264' }"
          style="border-left: 1px solid #E8E9E9;"
        >By Entity</button>
      </div>

      <ExportMenu :disabled="!assignments.length" :exports="[{ type: 'excel', action: exportAssignmentsToExcel }]" />
    </div>

    <!-- Unreachable-assignment warning. An assignment rule only delivers work if
         the member's login ALSO has entity access; where it doesn't, the member's
         queue is silently empty. Renders nothing when no member is access-gated,
         which is why it stays quiet outside Coding. -->
    <div
      v-if="matchedGaps.length"
      class="mx-3 mt-3 px-3 py-2 flex items-center gap-2 text-xs rounded-md"
      style="background-color: #FFF7ED; border: 1px solid #FED7AA; color: #9A3412;"
    >
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>
      <!-- Deliberately says "login" rather than {{ memberLabel }}: Coding sets
           memberLabel to "Assignment" (it names the column, not the person), so
           interpolating it here reads as nonsense. -->
      <span>
        <strong>{{ matchedGaps.length }}</strong>
        {{ matchedGaps.length === 1 ? 'assignment is' : 'assignments are' }} unreachable —
        the assigned login cannot see the practice, so no work is delivered.
        {{ gappedMemberCount }} {{ gappedMemberCount === 1 ? 'login' : 'logins' }} affected.
      </span>
      <button
        class="ml-auto flex-shrink-0 px-2 py-0.5 rounded transition-colors"
        :style="onlyGaps
          ? { backgroundColor: '#9A3412', color: '#fff' }
          : { border: '1px solid #FED7AA', color: '#9A3412' }"
        @click="onlyGaps = !onlyGaps"
      >{{ onlyGaps ? 'Show all' : 'Show only these' }}</button>
    </div>

    <!-- Toggle write failure. Previously a failed PATCH only reached
         console.error, so a rejected write and a dead button were
         indistinguishable to the user. -->
    <div
      v-if="toggleError"
      class="mx-3 mt-3 px-3 py-2 flex items-center gap-2 text-xs rounded-md"
      style="background-color: #FEF2F2; border: 1px solid #FECACA; color: #991B1B;"
    >
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
      <span>{{ toggleError }}</span>
      <button class="ml-auto flex-shrink-0 px-2 py-0.5 rounded" style="border: 1px solid #FECACA; color: #991B1B;"
              @click="toggleError = null">Dismiss</button>
    </div>

    <!-- Assignment list -->
    <div class="flex-1 overflow-auto m-3" style="border: 1px solid #E8E9E9; border-radius: 8px;">
      <div v-if="loading" class="p-8 text-center text-xs" style="color: #626264;">Loading assignments...</div>

      <!-- By Rule view (flat table) -->
      <table v-else-if="viewMode === 'rules'" class="w-full text-xs" style="table-layout: fixed;">
        <colgroup>
          <col style="width: 12%;" />
          <col />
          <col />
          <col v-if="!hidePrograms" />
          <col />
          <col v-if="!hideStages" />
          <col v-if="!hideRole" style="width: 10%;" />
          <col style="width: 56px;" />
          <col style="width: 40px;" />
        </colgroup>
        <thead style="color: #626264; position: sticky; top: 0; z-index: 1; background-color: #fff; border-bottom: 1px solid #E8E9E9;">
          <tr>
            <th @click="toggleSort('entity_type_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              Entity Type <span class="text-xs">{{ sortIndicator('entity_type_name') }}</span>
            </th>
            <th @click="toggleSort('entity_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              Entity <span class="text-xs">{{ sortIndicator('entity_name') }}</span>
            </th>
            <th @click="toggleSort('member_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              {{ memberLabel }} <span class="text-xs">{{ sortIndicator('member_name') }}</span>
            </th>
            <th v-if="!hidePrograms" @click="toggleSort('metric_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              Program <span class="text-xs">{{ sortIndicator('metric_name') }}</span>
            </th>
            <th @click="toggleSort('contract_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              Contract <span class="text-xs">{{ sortIndicator('contract_name') }}</span>
            </th>
            <th v-if="!hideStages" @click="toggleSort('task_type_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              {{ stageLabel }} <span class="text-xs">{{ sortIndicator('task_type_name') }}</span>
            </th>
            <th v-if="!hideRole" @click="toggleSort('role_name')" class="py-2.5 px-3 text-left font-medium cursor-pointer select-none hover:text-gray-900">
              Role <span class="text-xs">{{ sortIndicator('role_name') }}</span>
            </th>
            <th class="py-2.5 px-2 text-center font-medium">Active</th>
            <th class="py-2.5 px-2"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in sortedAssignments"
            :key="row.id"
            class="hover:bg-gray-50"
            :style="{ borderBottom: '1px solid #F3F4F6', opacity: row.is_active ? 1 : 0.45 }"
          >
            <td class="py-2.5 px-3 text-left truncate" style="color: #A5A5A6;">{{ row.entity_type_name || '—' }}</td>
            <td class="sensitive py-2.5 px-3 text-left truncate" style="color: #353537;">
              <GapWarning :gap="ruleGap(row)" />{{ row.entity_name || 'All' }}
            </td>
            <td class="sensitive py-2.5 px-3 text-left truncate" style="color: #626264;">{{ row.member_name || '—' }}</td>
            <td v-if="!hidePrograms" class="py-2.5 px-3 text-left truncate" style="color: #626264;">{{ row.metric_name || 'All' }}</td>
            <td class="py-2.5 px-3 text-left truncate" style="color: #626264;">{{ row.contract_name || 'All' }}</td>
            <td v-if="!hideStages" class="py-2.5 px-3 text-left truncate" style="color: #626264;">{{ row.task_type_name || 'All' }}</td>
            <td v-if="!hideRole" class="py-2.5 px-3 text-left truncate" style="color: #626264;">{{ row.role_name || '—' }}</td>
            <td class="py-2.5 px-2 text-center">
              <button
                @click="toggleActive(row)"
                :disabled="isRulePending(row)"
                class="relative inline-flex h-4 w-7 items-center rounded-full transition-colors"
                :class="isRulePending(row) ? 'cursor-wait opacity-70' : ''"
                :style="{ backgroundColor: row.is_active ? '#22C55E' : '#D1D5DB' }"
                :title="isRulePending(row) ? 'Saving…' : (row.is_active ? 'Deactivate' : 'Activate')"
              >
                <!-- Saving: the knob becomes a spinner in place, so the control
                     itself shows work is happening. Previously nothing changed
                     between click and response and the button looked inert. -->
                <span
                  v-if="isRulePending(row)"
                  class="inline-block h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin"
                  :class="row.is_active ? 'translate-x-3.5' : 'translate-x-0.5'"
                />
                <span
                  v-else
                  class="inline-block h-3 w-3 rounded-full bg-white transition-transform"
                  :class="row.is_active ? 'translate-x-3.5' : 'translate-x-0.5'"
                />
              </button>
            </td>
            <td class="py-2.5 px-2 text-center">
              <button
                @click="removeAssignment(row)"
                class="text-gray-300 hover:text-red-500 transition-colors"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </td>
          </tr>
          <tr v-if="sortedAssignments.length === 0">
            <td :colspan="9" class="py-8 text-center text-xs" style="color: #626264;">
              No assignments yet. Click "Assign" to get started.
            </td>
          </tr>
        </tbody>
      </table>

      <!-- By Member view (columnar table with grouped rows) -->
      <table v-else-if="viewMode === 'member'" class="w-full text-xs" style="table-layout: fixed;">
        <colgroup>
          <col style="width: 28px;" />
          <col />
          <col v-if="!hidePrograms" />
          <col v-if="!hideStages" />
          <col />
          <col v-if="showQueueLoad" style="width: 150px;" />
          <col v-if="showQueueLoad" style="width: 90px;" />
          <col v-if="!hideRole" style="width: 10%;" />
          <col style="width: 56px;" />
          <col style="width: 40px;" />
        </colgroup>
        <thead style="color: #626264; position: sticky; top: 0; z-index: 1; background-color: #fff; border-bottom: 1px solid #E8E9E9;">
          <tr>
            <th class="py-2.5 px-2"></th>
            <th class="py-2.5 px-3 text-left font-medium">Entity</th>
            <th v-if="!hidePrograms" class="py-2.5 px-3 text-left font-medium">Program</th>
            <th v-if="!hideStages" class="py-2.5 px-3 text-left font-medium">{{ stageLabel }}</th>
            <th class="py-2.5 px-3 text-left font-medium">Contract</th>
            <th
              v-if="showQueueLoad"
              class="py-2.5 px-3 text-right font-medium"
              title="Charts this coder is holding here. Nearly all held charts are On Hold — a claimed chart is a parked chart, not one being worked."
            >Claimed</th>
            <th
              v-if="showQueueLoad"
              class="py-2.5 px-3 text-right font-medium"
              title="Live charts here that nobody has claimed — what grab-new can still serve. This is a SHARED queue depth: every coder assigned this practice and tier sees the same charts, so do not add it up across coders."
            >Available</th>
            <th v-if="!hideRole" class="py-2.5 px-3 text-left font-medium">Role</th>
            <th class="py-2.5 px-2 text-center font-medium">Active</th>
            <th class="py-2.5 px-2"></th>
          </tr>
        </thead>
        <tbody v-if="groupedByMember.length === 0">
          <tr>
            <td :colspan="memberColCount" class="py-8 text-center text-xs" style="color: #626264;">
              No assignments yet. Click "Assign" to get started.
            </td>
          </tr>
        </tbody>
        <tbody v-for="group in groupedByMember" :key="group.member_id">
          <!-- Member header row -->
          <tr
            class="cursor-pointer hover:bg-gray-50 select-none"
            :style="{ borderBottom: '1px solid #E8E9E9', opacity: memberAnyActive(group) ? 1 : 0.45 }"
            @click="toggleMemberExpand(group.member_id)"
          >
            <td class="py-2.5 px-2 text-center">
              <svg
                class="w-3 h-3 transition-transform inline-block"
                :class="expandedMembers.includes(group.member_id) ? 'rotate-90' : ''"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                style="color: #A5A5A6;"
              ><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </td>
            <td class="py-2.5 px-3 text-left font-medium" :colspan="(hidePrograms && hideStages ? 1 : hidePrograms || hideStages ? 2 : 3) + 1" style="color: #353537;">
              <span class="sensitive">{{ group.member_name }}</span>
              <span class="ml-2 font-normal text-xs px-1.5 py-0.5 rounded-full" style="background-color: #ECEDEF; color: #626264;">{{ group.rules.length }}</span>
              <span
                v-if="memberGapCount(group)"
                class="ml-1.5 font-normal text-xs px-1.5 py-0.5 rounded-full"
                style="background-color: #FFF7ED; color: #9A3412; border: 1px solid #FED7AA;"
                :title="`${memberGapCount(group)} of these ${group.rules.length} assignments are at practices this login cannot see.`"
              >{{ memberGapCount(group) }} unreachable</span>
            </td>
            <td
              v-if="showQueueLoad"
              class="py-2.5 px-3 text-right tabular-nums"
              style="color: #626264;"
              title="Every chart this coder is holding across all their assignments, active or not."
            >{{ memberTotals(group) ? claimedLabel(memberTotals(group)) : '—' }}</td>
            <td
              v-if="showQueueLoad"
              class="py-2.5 px-3 text-right tabular-nums"
              style="color: #626264;"
              title="Unclaimed charts across this coder's ACTIVE assignments only — inactive rules deliver nothing, so they are excluded here."
            >{{ memberTotals(group) ? memberTotals(group).available : '—' }}</td>
            <td v-if="!hideRole" class="py-2.5 px-3 text-left truncate" style="color: #626264;">{{ group.role || '—' }}</td>
            <td class="py-2.5 px-2 text-center">
              <button
                @click.stop="toggleMemberActive(group)"
                :disabled="isMemberPending(group)"
                class="relative inline-flex h-4 w-7 items-center rounded-full transition-colors"
                :class="isMemberPending(group) ? 'cursor-wait opacity-70' : ''"
                :style="{ backgroundColor: memberAllActive(group) ? '#22C55E' : memberAnyActive(group) ? '#F59E0B' : '#D1D5DB' }"
                :title="isMemberPending(group)
                  ? `Saving ${group.rules.length} rules…`
                  : (memberAllActive(group) ? 'Deactivate all rules' : 'Activate all rules')"
              >
                <span
                  v-if="isMemberPending(group)"
                  class="inline-block h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin"
                  :class="memberAnyActive(group) ? 'translate-x-3.5' : 'translate-x-0.5'"
                />
                <span
                  v-else
                  class="inline-block h-3 w-3 rounded-full bg-white transition-transform"
                  :class="memberAnyActive(group) ? 'translate-x-3.5' : 'translate-x-0.5'"
                />
              </button>
            </td>
            <td class="py-2.5 px-2"></td>
          </tr>
          <!-- Expanded rule rows -->
          <template v-if="expandedMembers.includes(group.member_id)">
            <tr
              v-for="rule in group.rules"
              :key="rule.id"
              class="hover:bg-gray-50"
              :style="{ borderBottom: '1px solid #F3F4F6', opacity: rule.is_active ? 1 : 0.45 }"
            >
              <td class="py-2 px-2"></td>
              <td class="sensitive py-2 px-3 text-left truncate" style="color: #353537;">
                <GapWarning :gap="ruleGap(rule)" />{{ rule.entity_name || 'All' }}
              </td>
              <td v-if="!hidePrograms" class="py-2 px-3 text-left truncate" style="color: #626264;">{{ rule.metric_name || 'All' }}</td>
              <td v-if="!hideStages" class="py-2 px-3 text-left truncate" style="color: #626264;">{{ rule.task_type_name || 'All' }}</td>
              <td class="py-2 px-3 text-left truncate" style="color: #626264;" :title="rule.contract_names && rule.contract_names.length ? rule.contract_names.join(', ') : ''">{{ rule.contract_label }}</td>
              <td v-if="showQueueLoad" class="py-2 px-3 text-right tabular-nums" style="color: #626264;">
                {{ ruleLoad(rule) ? claimedLabel(ruleLoad(rule)) : '—' }}
              </td>
              <td v-if="showQueueLoad" class="py-2 px-3 text-right tabular-nums" style="color: #626264;">
                <template v-if="!ruleLoad(rule)">—</template>
                <template v-else-if="rule.is_active">{{ ruleLoad(rule).available }}</template>
                <template v-else>({{ ruleLoad(rule).available }})</template>
              </td>
              <td v-if="!hideRole" class="py-2 px-3 text-left truncate" style="color: #626264;">{{ rule.role_name || '—' }}</td>
              <td class="py-2 px-2 text-center">
                <button
                  @click="toggleActive(rule)"
                  :disabled="isRulePending(rule)"
                  class="relative inline-flex h-4 w-7 items-center rounded-full transition-colors"
                  :class="isRulePending(rule) ? 'cursor-wait opacity-70' : ''"
                  :style="{ backgroundColor: rule.is_active ? '#22C55E' : '#D1D5DB' }"
                  :title="isRulePending(rule) ? 'Saving…' : (rule.is_active ? 'Deactivate' : 'Activate')"
                >
                  <span
                    v-if="isRulePending(rule)"
                    class="inline-block h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin"
                    :class="rule.is_active ? 'translate-x-3.5' : 'translate-x-0.5'"
                  />
                  <span
                    v-else
                    class="inline-block h-3 w-3 rounded-full bg-white transition-transform"
                    :class="rule.is_active ? 'translate-x-3.5' : 'translate-x-0.5'"
                  />
                </button>
              </td>
              <td class="py-2 px-2 text-center">
                <button
                  @click="removeAssignment(rule)"
                  class="text-gray-300 hover:text-red-500 transition-colors"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- By Entity view (coverage matrix) -->
      <div v-else-if="viewMode === 'entity'">
        <table class="text-xs" style="min-width: 100%;">
          <thead style="color: #626264; position: sticky; top: 0; z-index: 1; background-color: #fff; border-bottom: 1px solid #E8E9E9;">
            <tr>
              <th class="py-2.5 px-3 text-left font-medium whitespace-nowrap" style="position: sticky; left: 0; background-color: #fff; z-index: 2;">Practice</th>
              <template v-for="program in entityViewPrograms" :key="'ph-' + program">
                <th
                  v-for="tt in entityViewTaskTypes.filter(t => t.program === program)"
                  :key="'th-' + tt.id"
                  class="py-2.5 px-2 text-center font-medium whitespace-nowrap"
                  :style="{ backgroundColor: programColors[program]?.bg || '#fff', minWidth: '90px' }"
                >
                  <div class="text-[10px] leading-tight" :style="{ color: programColors[program]?.text || '#A5A5A6' }">{{ program }}</div>
                  <div>{{ tt.tier }}</div>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in entityViewRows"
              :key="row.id"
              class="hover:bg-gray-50"
              style="border-bottom: 1px solid #F3F4F6;"
            >
              <td class="sensitive py-2 px-3 text-left truncate whitespace-nowrap font-medium" style="color: #353537; position: sticky; left: 0; background-color: #fff; z-index: 1; max-width: 220px;">{{ row.name }}</td>
              <template v-for="program in entityViewPrograms" :key="'pb-' + program + '-' + row.id">
                <td
                  v-for="tt in entityViewTaskTypes.filter(t => t.program === program)"
                  :key="'td-' + tt.id + '-' + row.id"
                  class="py-2 px-2 text-center"
                  :style="{ backgroundColor: programColors[program]?.bg || 'transparent' }"
                  :title="cellTitle(row, program, tt)"
                >
                  <template v-if="row.coverage[program + '::' + tt.id]?.has">
                    <!-- Assigned but nobody assigned here can see the practice:
                         a green check would read as covered when nothing ships. -->
                    <span class="inline-flex items-center gap-1 whitespace-nowrap">
                      <svg
                        v-if="!row.coverage[program + '::' + tt.id].reachable"
                        class="w-3.5 h-3.5 shrink-0" fill="none" stroke="#B45309" viewBox="0 0 24 24" stroke-width="2"
                      ><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>
                      <svg v-else class="w-3.5 h-3.5 shrink-0" fill="none" stroke="#22C55E" viewBox="0 0 24 24" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                      <span v-if="showQueueLoad && entityCellAvail(row.id, program, tt.id) > 0" class="text-[11px] leading-none tabular-nums" style="color: #626264;">({{ entityCellAvail(row.id, program, tt.id) }} open)</span>
                    </span>
                  </template>
                  <template v-else-if="showQueueLoad && entityCellAvail(row.id, program, tt.id) > 0">
                    <!-- Open charts here but no assignment routes them: nobody
                         can grab-new this queue until someone is assigned. -->
                    <span class="inline-flex items-center gap-1 whitespace-nowrap">
                      <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="#B45309" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>
                      <span class="text-[11px] leading-none tabular-nums font-medium" style="color: #B45309;">({{ entityCellAvail(row.id, program, tt.id) }} open)</span>
                    </span>
                  </template>
                  <template v-else>
                    <span style="color: #D1D5DB;">—</span>
                  </template>
                </td>
              </template>
            </tr>
            <tr v-if="entityViewRows.length === 0">
              <td :colspan="1 + entityViewTaskTypes.length" class="py-8 text-center text-xs" style="color: #626264;">
                No entity assignments found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Assign Modal -->
    <div v-if="showAssignModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(0,0,0,0.3);">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 flex flex-col" style="border: 1px solid #E8E9E9; height: 80vh;">
        <div class="px-5 py-3 flex items-center justify-between flex-shrink-0" style="border-bottom: 1px solid #E8E9E9;">
          <h3 class="text-xs font-semibold" style="color: #353537;">Assign {{ memberLabel }}s</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div class="flex-1 flex overflow-hidden">
          <!-- Left: Selectors -->
          <div class="w-1/2 flex flex-col overflow-y-auto p-4 space-y-4" style="border-right: 1px solid #E8E9E9;">
            <div v-if="!optionsLoaded" class="flex-1 flex items-center justify-center">
              <div class="text-xs" style="color: #A5A5A6;">Loading options...</div>
            </div>
            <template v-else>
            <!-- Member type (if multiple) -->
            <div v-if="memberEntityTypes.length > 1">
              <label class="block text-xs mb-1.5" style="color: #626264;">{{ memberLabel }} Type</label>
              <SimpleMultiSelect
                v-model="assignEntityType"
                :options="entityTypeOptions"
                placeholder="Select type..."
                :multiple="false"
                @change="onEntityTypeChange"
              />
            </div>

            <!-- Members -->
            <div>
              <label class="block text-xs mb-1.5" style="color: #626264;">{{ activeMemberTypeName || memberLabel }}</label>
              <SimpleMultiSelect
                v-model="assignMembers"
                :options="memberOptions"
                placeholder="Search..."
                :searchable="true"
                :dropdown-height="400"
              />
              <div v-if="membersLoading" class="text-xs mt-1" style="color: #A5A5A6;">Loading...</div>
            </div>

            <!-- Practices -->
            <div>
              <label class="block text-xs mb-1.5" style="color: #626264;">Practices</label>
              <EntitySearch_CareManagement
                @entities-selected="onPracticesSelected"
                :initial-selection="assignEntities"
              />
            </div>

            <!-- Contract (overrides practice routing) -->
            <div v-if="contractOptions.length">
              <label class="block text-xs mb-1.5" style="color: #626264;">Contract</label>
              <SimpleMultiSelect
                v-model="assignContracts"
                :options="contractOptions"
                placeholder="All contracts"
                :searchable="true"
              />
              <div class="text-xs mt-1" style="color: #A5A5A6;">Contract rules take priority over practice rules.</div>
            </div>

            <!-- Programs -->
            <div v-if="!hidePrograms">
              <label class="block text-xs mb-1.5" style="color: #626264;">Programs</label>
              <SimpleMultiSelect
                v-model="assignPrograms"
                :options="programOptions"
                placeholder="All programs"
              />
            </div>

            <!-- Stages / Tiers -->
            <div v-if="!hideStages">
              <label class="block text-xs mb-1.5" style="color: #626264;">{{ stageLabel }}</label>
              <SimpleMultiSelect
                v-model="assignTaskTypes"
                :options="taskTypeOptions"
                placeholder="All"
              />
            </div>

            <!-- Role -->
            <div v-if="!hideRole">
              <label class="block text-xs mb-1.5" style="color: #626264;">Role</label>
              <SimpleMultiSelect
                v-model="assignRole"
                :options="roleOptions"
                placeholder="Select role..."
                :searchable="true"
                :multiple="false"
              />
            </div>
            </template>
          </div>

          <!-- Right: Summary -->
          <div class="w-1/2 flex flex-col overflow-y-auto p-4 space-y-4">
            <div>
              <div class="text-xs mb-1.5" style="color: #626264;">Selected {{ activeMemberTypeName || memberLabel }}s <span style="color: #A5A5A6;">({{ assignMembers.length }})</span></div>
              <div v-if="assignMembers.length" class="flex flex-wrap gap-1.5">
                <span v-for="id in assignMembers" :key="'m-' + id"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style="background-color: #FEF3F0; color: #353537;"
                >
                  {{ memberName(id) }}
                  <button @click="removeFrom('assignMembers', id)" class="hover:text-red-500" style="color: #A5A5A6;">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </span>
              </div>
              <div v-else class="text-xs" style="color: #A5A5A6;">None selected</div>
            </div>

            <div>
              <div class="text-xs mb-1.5" style="color: #626264;">Selected Practices <span style="color: #A5A5A6;">({{ assignEntities.length }})</span></div>
              <div v-if="assignEntities.length" class="flex flex-wrap gap-1.5">
                <span v-for="id in assignEntities" :key="'e-' + id"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style="background-color: #F0F4FF; color: #353537;"
                >
                  {{ practiceName(id) }}
                  <button @click="removeFrom('assignEntities', id)" class="hover:text-red-500" style="color: #A5A5A6;">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </span>
              </div>
              <div v-else class="text-xs" style="color: #A5A5A6;">None selected — assigns across all practices</div>
            </div>

            <div v-if="contractOptions.length">
              <div class="text-xs mb-1.5" style="color: #626264;">Selected Contracts <span style="color: #A5A5A6;">({{ assignContracts.length }})</span></div>
              <div v-if="assignContracts.length" class="flex flex-wrap gap-1.5">
                <span v-for="id in assignContracts" :key="'c-' + id"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style="background-color: #FDF4FF; color: #353537;"
                >
                  {{ contractName(id) }}
                  <button @click="removeFrom('assignContracts', id)" class="hover:text-red-500" style="color: #A5A5A6;">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </span>
              </div>
              <div v-else class="text-xs" style="color: #A5A5A6;">All contracts</div>
            </div>

            <div v-if="!hidePrograms">
              <div class="text-xs mb-1.5" style="color: #626264;">Selected Programs <span style="color: #A5A5A6;">({{ assignPrograms.length }})</span></div>
              <div v-if="assignPrograms.length" class="flex flex-wrap gap-1.5">
                <span v-for="p in assignPrograms" :key="'p-' + p"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style="background-color: #F0FDF4; color: #353537;"
                >
                  {{ programLabel(p) }}
                  <button @click="removeFrom('assignPrograms', p)" class="hover:text-red-500" style="color: #A5A5A6;">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </span>
              </div>
              <div v-else class="text-xs" style="color: #A5A5A6;">All programs</div>
            </div>

            <div v-if="!hideStages">
              <div class="text-xs mb-1.5" style="color: #626264;">Selected {{ stageLabel }} <span style="color: #A5A5A6;">({{ assignTaskTypes.length }})</span></div>
              <div v-if="assignTaskTypes.length" class="flex flex-wrap gap-1.5">
                <span v-for="id in assignTaskTypes" :key="'tt-' + id"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style="background-color: #EFF6FF; color: #353537;"
                >
                  {{ taskTypeName(id) }}
                  <button @click="removeFrom('assignTaskTypes', id)" class="hover:text-red-500" style="color: #A5A5A6;">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </span>
              </div>
              <div v-else class="text-xs" style="color: #A5A5A6;">All</div>
            </div>

            <div v-if="!hideRole">
              <div class="text-xs mb-1.5" style="color: #626264;">Role</div>
              <div v-if="assignRole.length" class="text-xs" style="color: #353537;">
                {{ roleName(assignRole[0]) }}
                <button @click="assignRole = []" class="ml-1 hover:text-red-500" style="color: #A5A5A6;">
                  <svg class="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
              <div v-else class="text-xs" style="color: #A5A5A6;">No role selected</div>
            </div>

            <div v-if="assignResult" class="px-4 py-3 rounded-lg text-xs"
              :style="assignResult.error
                ? { backgroundColor: '#FEF2F2', color: '#DC2626' }
                : { backgroundColor: '#F0FDF4', color: '#166534' }"
            >
              {{ assignResult.error || `Created ${assignResult.assigned} assignments` }}
            </div>
          </div>
        </div>

        <div class="px-5 py-3 flex items-center justify-between flex-shrink-0" style="border-top: 1px solid #E8E9E9;">
          <div class="text-xs" style="color: #626264;">
            {{ assignMembers.length }} {{ activeMemberTypeName || memberLabel }}{{ assignMembers.length !== 1 ? 's' : '' }}
            × {{ assignEntities.length || 'all' }} practice{{ assignEntities.length !== 1 ? 's' : '' }}
            <template v-if="!hidePrograms">× {{ assignPrograms.length || 'all' }} program{{ assignPrograms.length !== 1 ? 's' : '' }}</template>
          </div>
          <div class="flex gap-3">
            <button @click="closeModal" class="px-4 py-1.5 text-xs rounded-lg" style="color: #626264; border: 1px solid #E8E9E9;">Cancel</button>
            <button
              @click="submitAssign"
              :disabled="!assignMembers.length || assignLoading"
              class="px-4 py-1.5 text-xs text-white rounded-lg transition-colors"
              :style="{ backgroundColor: assignMembers.length && !assignLoading ? '#F76734' : '#D1D5DB' }"
            >{{ assignLoading ? 'Assigning…' : 'Assign' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import EntitySearch_CareManagement from '@/components/widgetcomponents/Search_Widgets/EntitySearch_CareManagement.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import GapWarning from '@/components/widgetcomponents/Worklist_Assignment_GapWarning.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx';

export default {
  name: 'Worklist_Assignments',
  components: { SimpleMultiSelect, EntitySearch_CareManagement, ExportMenu, GapWarning },
  props: {
    // Config
    metricMap: { type: Object, default: () => ({}) },   // e.g. { 'Suspect': 34, 'Early Start': 50, 'Resub': 30 }
    memberEntityTypes: { type: Array, required: true }, // e.g. ['Coding Analyst', 'Coding Manager'] or ['Care Team Member']
    memberLabel: { type: String, default: 'Member' },   // e.g. 'Coder' or 'Team Member'
    stageLabel: { type: String, default: 'Stage' },      // e.g. 'Tier' or 'Stage'
    taskTypeMap: { type: Array, default: () => [] },     // e.g. [{ program: 'Suspect', tier: '1LR', id: 1 }, ...]
    fixedTaskTypeIds: { type: Array, default: () => [] }, // auto-applied to every assignment (e.g. [14] for quality)
    hidePrograms: { type: Boolean, default: false },     // hide Program column/filter/selector
    hideStages: { type: Boolean, default: false },       // hide Stage/Tier column/filter/selector
    hideRole: { type: Boolean, default: false },         // hide Role column/selector (coding has no assignment role)
    showQueueLoad: { type: Boolean, default: false },
    height: { type: String, default: 'auto' },
  },
  data() {
    return {
      assignments: [],
      loading: false,
      programFilter: [],
      sortCol: 'entity_name',
      sortDir: 'asc',
      viewMode: 'member',
      expandedMembers: [],
      visibilityGaps: [],
      onlyGaps: false,

      queueLoad: { rules: {}, entities: {} },

      // In-flight toggle tracking. A toggle click fires a PATCH/bulk-set-active
      // and then mutates is_active ONLY on success, so between click and
      // response the switch shows the OLD state and the UI looks dead. On a
      // 3,842-row table that read as "the toggle buttons do not work" (Chris
      // Weigert, 2026-07-27/28) even though every write returned 200.
      // Keyed by rule id for row toggles and by member_id for the member-level
      // toggle. A Set, but reassigned (not mutated) so Vue reactivity fires.
      pendingRuleIds: new Set(),
      pendingMemberIds: new Set(),
      toggleError: null,


      showAssignModal: false,
      assignEntityType: [],
      assignMembers: [],
      assignEntities: [],
      assignContracts: [],
      assignPrograms: [],
      assignTaskTypes: [],
      assignRole: [],
      assignLoading: false,
      assignResult: null,
      membersLoading: false,

      contracts: [],
      entityTypes: [],
      memberEntitiesLoaded: [],
      practiceEntities: [],
      selectedEntityNames: {},
      roles: [],
      optionsLoaded: false,


    };
  },
  computed: {
    allMetricIds() {
      return Object.values(this.metricMap);
    },
    programOptions() {
      return Object.entries(this.metricMap).map(([label, id]) => ({ value: label, label }));
    },
    contractOptions() {
      return this.contracts.map(c => ({ value: c.id, label: c.name }));
    },
    metricIds() {
      if (!this.programFilter.length) return this.allMetricIds;
      return this.programFilter.map(v => this.metricMap[v]).filter(Boolean);
    },
    entityTypeOptions() {
      return this.entityTypes
        .filter(et => this.memberEntityTypes.includes(et.type_name))
        .map(et => ({ value: et.id, label: et.type_name }));
    },
    activeMemberTypeName() {
      if (this.memberEntityTypes.length === 1) return this.memberEntityTypes[0];
      if (!this.assignEntityType.length) return '';
      const et = this.entityTypes.find(t => t.id === this.assignEntityType[0]);
      return et ? et.type_name : '';
    },
    memberOptions() {
      return this.memberEntitiesLoaded.map(e => ({ value: e.id, label: e.name }));
    },
    practiceEntityOptions() {
      return this.practiceEntities.map(e => ({ value: e.id, label: e.name }));
    },
    taskTypeOptions() {
      if (this.taskTypeMap.length) {
        const programs = this.assignPrograms.length ? this.assignPrograms : Object.keys(this.metricMap);
        const filtered = this.taskTypeMap.filter(t => programs.includes(t.program));
        return filtered.map(t => ({
          value: t.id,
          label: programs.length === 1 ? t.tier : `${t.program} ${t.tier}`,
        }));
      }
      // Fallback: fetch from loaded task types
      return this._fetchedTaskTypes.map(tt => ({ value: tt.id, label: tt.name }));
    },
    roleOptions() {
      return this.roles.map(r => ({ value: r.id, label: r.name }));
    },
    // (member, entity) -> gap. One warning per pair, not per rule, so a coder
    // assigned the same practice across four tiers is flagged once per row
    // rather than presented as four separate problems.
    gapByPair() {
      const map = {};
      for (const g of this.visibilityGaps) {
        map[`${g.member_id}::${g.entity_id}`] = g;
      }
      return map;
    },
    // The gaps the loaded rules actually hit, deduped by (member, entity). The
    // banner counts THESE rather than the raw API list so the headline can never
    // claim more problems than are marked on screen — the endpoint is filtered
    // independently of the list, and a caller that filters one but not the other
    // would otherwise produce a count with nothing to point at.
    matchedGaps() {
      const seen = new Set();
      const out = [];
      for (const r of this.assignments) {
        const gap = this.ruleGap(r);
        if (!gap) continue;
        const key = `${gap.member_id}::${gap.entity_id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(gap);
      }
      return out;
    },
    gappedMemberCount() {
      return new Set(this.matchedGaps.map(g => g.member_id)).size;
    },
    memberColCount() {
      return 5
        + (this.hidePrograms ? 0 : 1)
        + (this.hideStages ? 0 : 1)
        + (this.hideRole ? 0 : 1)
        + (this.showQueueLoad ? 2 : 0);
    },
    groupedByMember() {
      const groups = {};
      for (const r of this.visibleAssignments) {
        const key = r.member || r.member_name || 'Unknown';
        if (!groups[key]) {
          groups[key] = {
            member_id: r.member,
            member_name: r.member_name,
            role: r.role_name,
            role_id: r.role,
            rawRules: [],
            entity_ids: new Set(),
            metric_ids: new Set(),
            task_type_ids: new Set(),
          };
        }
        groups[key].rawRules.push(r);
        if (r.entity) groups[key].entity_ids.add(r.entity);
        if (r.metric) groups[key].metric_ids.add(r.metric);
        if (r.task_type) groups[key].task_type_ids.add(r.task_type);
      }
      // Collapse rules that are identical except for CONTRACT into a single
      // logical line. A single (entity, program, stage, role) assignment that
      // was written as N per-contract rows (e.g. all ACOs selected) is ONE
      // assignment to the user — showing it as N identical-looking lines reads
      // as "duplicates" (Shannon, 2026-07-21). The contract dimension is
      // preserved and surfaced in a Contract cell ("All contracts" / a single
      // contract name / "N contracts"); the underlying row ids are kept on
      // `ruleIds` so toggle/delete act on every collapsed row.
      for (const g of Object.values(groups)) {
        g.entity_ids = Array.from(g.entity_ids);
        g.metric_ids = Array.from(g.metric_ids);
        g.task_type_ids = Array.from(g.task_type_ids);

        const collapsed = {};
        for (const r of g.rawRules) {
          const ck = [r.entity, r.metric, r.task_type, r.role].join('|');
          if (!collapsed[ck]) {
            collapsed[ck] = {
              id: r.id,               // representative row (for :key)
              ruleIds: [],
              member: r.member,
              member_name: r.member_name,
              entity: r.entity,
              entity_name: r.entity_name,
              metric: r.metric,
              metric_name: r.metric_name,
              task_type: r.task_type,
              task_type_name: r.task_type_name,
              role: r.role,
              role_name: r.role_name,
              contract_ids: new Set(),
              contract_names: new Set(),
              anyNullContract: false,
              is_active: false,       // active if ANY underlying row is active
            };
          }
          const c = collapsed[ck];
          c.ruleIds.push(r.id);
          if (r.contract) { c.contract_ids.add(r.contract); if (r.contract_name) c.contract_names.add(r.contract_name); }
          else c.anyNullContract = true;
          if (r.is_active) c.is_active = true;
        }
        g.rules = Object.values(collapsed).map(c => {
          const nContracts = c.contract_ids.size;
          let contract_label;
          if (c.anyNullContract || nContracts === 0) contract_label = 'All contracts';
          else if (nContracts === 1) contract_label = Array.from(c.contract_names)[0] || '1 contract';
          else contract_label = `${nContracts} contracts`;
          return {
            ...c,
            contract_ids: Array.from(c.contract_ids),
            contract_names: Array.from(c.contract_names),
            contract_label,
          };
        });
        g.rules.sort((a, b) =>
          (a.entity_name || '').localeCompare(b.entity_name || '') ||
          (a.metric_name || '').localeCompare(b.metric_name || '') ||
          (a.task_type_name || '').localeCompare(b.task_type_name || '')
        );
      }
      return Object.values(groups).sort((a, b) => (a.member_name || '').localeCompare(b.member_name || ''));
    },
    // Every view renders from this rather than `assignments` so the banner's
    // "Show only these" narrows all three consistently.
    visibleAssignments() {
      if (!this.onlyGaps) return this.assignments;
      return this.assignments.filter(r => this.ruleGap(r));
    },
    sortedAssignments() {
      const col = this.sortCol;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return [...this.visibleAssignments].sort((a, b) => {
        const av = (a[col] || '').toLowerCase();
        const bv = (b[col] || '').toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
    entityViewPrograms() {
      // Programs that have task types in taskTypeMap
      const programs = [...new Set(this.taskTypeMap.map(t => t.program))];
      // If program filter is active, only show those
      if (this.programFilter.length) return programs.filter(p => this.programFilter.includes(p));
      return programs;
    },
    entityViewTaskTypes() {
      // Filtered task types grouped by program
      const programs = this.entityViewPrograms;
      return this.taskTypeMap.filter(t => programs.includes(t.program));
    },
    programColors() {
      const palette = [
        { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' },  // blue
        { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534' },  // green
        { bg: '#FFF7ED', border: '#FED7AA', text: '#9A3412' },  // orange
        { bg: '#FAF5FF', border: '#E9D5FF', text: '#6B21A8' },  // purple
        { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' },  // red
        { bg: '#F0FDFA', border: '#99F6E4', text: '#115E59' },  // teal
      ];
      const map = {};
      this.entityViewPrograms.forEach((p, i) => { map[p] = palette[i % palette.length]; });
      return map;
    },
    entityViewRows() {
      // Group assignments by entity, then build a coverage map per entity
      // coverage[entityId][metricId][taskTypeId] = true
      const metricMap = this.metricMap; // { 'TOC': 61, 'VC+': 60, ... }
      const programByMetricId = {};
      for (const [label, id] of Object.entries(metricMap)) {
        programByMetricId[id] = label;
      }

      // Coverage key is program::taskTypeId to avoid cross-program bleed
      // when different programs share the same task type IDs
      const entities = {}; // entityId → { id, name, type_name, coverage: { 'program::ttId': { has: bool, members: [], reachable: int } } }
      // A cell is "covered" by whoever is assigned it, but only the members who
      // can actually SEE the practice will ever be served work there. Counting
      // reachable separately is what lets the matrix distinguish a real check
      // from one that looks like coverage and delivers nothing.
      const mark = (ent, key, r) => {
        if (!ent.coverage[key]) ent.coverage[key] = { has: true, members: [], reachable: 0 };
        const cell = ent.coverage[key];
        cell.has = true;
        if (r.member_name && !cell.members.includes(r.member_name)) {
          cell.members.push(r.member_name);
          if (!this.ruleGap(r)) cell.reachable += 1;
        }
      };
      for (const r of this.visibleAssignments) {
        if (!r.entity) continue;
        if (!entities[r.entity]) {
          entities[r.entity] = { id: r.entity, name: r.entity_name, type_name: r.entity_type_name, coverage: {} };
        }
        const ent = entities[r.entity];
        // If rule has task_type, mark that specific task type within matching programs only
        if (r.task_type) {
          const program = r.metric ? programByMetricId[r.metric] : null;
          if (program) {
            mark(ent, `${program}::${r.task_type}`, r);
          } else {
            // No metric on rule — covers this task type across all programs that use it
            for (const tt of this.taskTypeMap.filter(t => t.id === r.task_type)) {
              mark(ent, `${tt.program}::${r.task_type}`, r);
            }
          }
        } else if (r.metric) {
          // Rule covers ALL task types for this metric's program only
          const program = programByMetricId[r.metric];
          if (program) {
            for (const tt of this.taskTypeMap.filter(t => t.program === program)) {
              mark(ent, `${program}::${tt.id}`, r);
            }
          }
        }
      }

      // Union in practices that have OPEN work but no assignment anywhere — the
      // orphaned-queue case. They get no coverage cells, so every column is a
      // dash; the availability count + warning renders on those dashes.
      if (this.showQueueLoad) {
        for (const [eid, ent] of Object.entries(this.queueLoad.entities)) {
          if (!entities[eid]) {
            entities[eid] = { id: Number(eid), name: ent.name, type_name: ent.type_name || 'Practice', coverage: {} };
          }
        }
      }

      // Filter to entity type "Practice" by default (or all if none match)
      let rows = Object.values(entities);
      const practiceRows = rows.filter(r => r.type_name === 'Practice');
      if (practiceRows.length) rows = practiceRows;

      // Hide rows with no coverage AND no open work for any visible column. A row
      // survives if some visible cell is assigned (coverage) or has unclaimed
      // charts (the orphaned queue surfaced under showQueueLoad).
      rows = rows.filter(r => this.entityViewTaskTypes.some((tt) => {
        return !!r.coverage[`${tt.program}::${tt.id}`]?.has
          || (this.showQueueLoad && this.entityCellAvail(r.id, tt.program, tt.id) > 0);
      }));

      return rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
  },
  methods: {
    // Both the flat rows and the contract-collapsed rows carry member+entity,
    // so one lookup serves every view.
    ruleGap(row) {
      if (!row || !row.member || !row.entity) return null;
      return this.gapByPair[`${row.member}::${row.entity}`] || null;
    },
    memberGapCount(group) {
      return group.rules.filter(r => this.ruleGap(r)).length;
    },
    ruleLoad(rule) {
      return this.queueLoad.rules[rule.id] || null;
    },
    claimedLabel(load) {
      if (!load || !load.claimed) return '0';
      return `${load.claimed} (${load.on_hold} on hold)`;
    },
    memberTotals(group) {
      if (!this.showQueueLoad) return null;
      let claimed = 0, onHold = 0, available = 0, seen = false;
      for (const rule of group.rules) {
        const load = this.ruleLoad(rule);
        if (!load) continue;
        seen = true;
        claimed += load.claimed;
        onHold += load.on_hold;
        if (rule.is_active) available += load.available;
      }
      return seen ? { claimed, on_hold: onHold, available } : null;
    },
    entityCellAvail(entityId, program, ttId) {
      const ent = this.queueLoad.entities[entityId];
      if (!ent || !ent.available) return 0;
      const mid = this.metricMap[program];
      if (mid == null) return 0;
      return ent.available[`${mid}::${ttId}`] || 0;
    },
    cellTitle(row, program, tt) {
      if (this.$root.isDemoMode) return '';
      const cell = row.coverage[`${program}::${tt.id}`];
      const avail = this.showQueueLoad ? this.entityCellAvail(row.id, program, tt.id) : 0;
      if (!cell) {
        if (avail > 0) {
          return `${avail} open ${avail === 1 ? 'chart' : 'charts'} here — no one is assigned to pull them.`;
        }
        return '';
      }
      const who = cell.members.join(', ');
      const suffix = avail > 0 ? ` · ${avail} available` : '';
      if (cell.has && !cell.reachable) {
        return `${who} — assigned, but none of them can see this practice, so no work is delivered here.${suffix}`;
      }
      return who + suffix;
    },
    exportAssignmentsToExcel() {
      const rows = this.sortedAssignments.map(row => {
        const out = {
          'Entity Type': row.entity_type_name || '',
          Entity: row.entity_name || 'All',
          [this.memberLabel]: row.member_name || '',
        };
        if (!this.hidePrograms) out.Program = row.metric_name || 'All';
        out.Contract = row.contract_name || 'All';
        if (!this.hideStages) out[this.stageLabel] = row.task_type_name || 'All';
        if (!this.hideRole) out.Role = row.role_name || '';
        out.Active = row.is_active ? 'Yes' : 'No';
        // The export is how this list gets actioned off-screen, so the warning
        // has to survive the trip out of the UI.
        if (this.matchedGaps.length) {
          const gap = this.ruleGap(row);
          out.Reachable = gap ? 'No' : 'Yes';
          out['Unreachable Reason'] = gap ? gap.reason : '';
        }
        return out;
      });
      if (!rows.length) return;
      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `assignments_${today}.xlsx`, { sheetName: 'Assignments' });
    },
    memberName(id) {
      const e = this.memberEntitiesLoaded.find(e => e.id === id);
      return e ? e.name : id;
    },
    practiceName(id) {
      if (this.selectedEntityNames[id]) return this.selectedEntityNames[id];
      const e = this.practiceEntities.find(e => e.id === id);
      return e ? e.name : id;
    },
    taskTypeName(id) {
      const opt = this.taskTypeOptions.find(o => o.value === id);
      return opt ? opt.label : id;
    },
    roleName(id) {
      const r = this.roles.find(r => r.id === id);
      return r ? r.name : id;
    },
    programLabel(val) {
      const opt = this.programOptions.find(o => o.value === val);
      return opt ? opt.label : val;
    },
    contractName(id) {
      const c = this.contracts.find(c => c.id === id);
      return c ? c.name : id;
    },
    removeFrom(field, val) {
      this[field] = this[field].filter(v => v !== val);
    },
    toggleMemberExpand(memberId) {
      const idx = this.expandedMembers.indexOf(memberId);
      if (idx >= 0) {
        this.expandedMembers.splice(idx, 1);
      } else {
        this.expandedMembers.push(memberId);
      }
    },
    toggleSort(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortCol = col;
        this.sortDir = 'asc';
      }
    },
    sortIndicator(col) {
      if (this.sortCol !== col) return '';
      return this.sortDir === 'asc' ? '▲' : '▼';
    },

    async loadAssignments() {
      this.loading = true;
      try {
        const params = { rules_only: 'true' };
        if (this.metricIds.length) params.metric_ids = this.metricIds.join(',');
        if (this.fixedTaskTypeIds.length) params.task_type_ids = this.fixedTaskTypeIds.join(',');
        this.loadQueueLoad(params);
        const [{ data }] = await Promise.all([
          this.$axios.get('/api/team-assignments/', { params }),
          this.loadVisibilityGaps(params),
        ]);
        const results = data.results || data;
        this.assignments = results.map(r => ({
          id: r.id,
          member: r.member,
          member_name: r.member_name || null,
          entity: r.entity,
          entity_name: r.entity_name || null,
          entity_type_name: r.entity_type_name || null,
          metric: r.metric,
          metric_name: r.metric_name || null,
          task_type: r.task_type,
          task_type_name: r.task_type_name || null,
          contract: r.contract || null,
          contract_name: r.contract_name || null,
          role: r.role,
          role_name: r.role_name || null,
          is_active: r.is_active !== false,
        }));
      } catch (err) {
        console.error('Error loading assignments:', err);
        this.assignments = [];
      } finally {
        this.loading = false;
      }
    },

    // Kept separate and non-throwing: the warning is a diagnostic overlay, so a
    // failure here must degrade to "no warnings" rather than blank the tab.
    async loadVisibilityGaps(params) {
      try {
        const { data } = await this.$axios.get('/api/team-assignments/visibility-gaps/', {
          params: { metric_ids: params.metric_ids, task_type_ids: params.task_type_ids },
        });
        this.visibilityGaps = data.gaps || [];
      } catch (err) {
        console.error('Error loading visibility gaps:', err);
        this.visibilityGaps = [];
      }
      if (!this.visibilityGaps.length) this.onlyGaps = false;
    },

    async loadQueueLoad(params) {
      if (!this.showQueueLoad) return;
      try {
        const { data } = await this.$axios.get('/api/team-assignments/queue-load/', {
          params: { metric_ids: params.metric_ids, task_type_ids: params.task_type_ids },
        });
        this.queueLoad = { rules: data.rules || {}, entities: data.entities || {} };
      } catch (err) {
        console.error('Error loading queue load:', err);
        this.queueLoad = { rules: {}, entities: {} };
      }
    },

    isRulePending(row) {
      const ids = row.ruleIds && row.ruleIds.length ? row.ruleIds : [row.id];
      return ids.some(id => this.pendingRuleIds.has(id));
    },
    isMemberPending(group) {
      return this.pendingMemberIds.has(group.member_id);
    },

    async toggleActive(row) {
      const newVal = !row.is_active;
      // A By-Member line may represent several underlying rule rows collapsed
      // over the contract dimension (row.ruleIds). Toggle them all together via
      // the batch endpoint so the single visible line stays in sync.
      const ids = row.ruleIds && row.ruleIds.length ? row.ruleIds : [row.id];

      // Ignore repeat clicks while this row's write is in flight. Without this,
      // an impatient double-click sends two opposing writes and the row lands
      // back where it started — which is exactly what happened to rules 102360
      // and 117966 on 2026-07-27 (each PATCHed twice, net no change).
      if (this.isRulePending(row)) return;

      // Mark pending BEFORE awaiting so the spinner renders on the same tick.
      this.pendingRuleIds = new Set([...this.pendingRuleIds, ...ids]);
      this.toggleError = null;
      try {
        if (ids.length > 1) {
          await this.$axios.post('/api/team-assignments/bulk-set-active/', { ids, is_active: newVal });
        } else {
          await this.$axios.patch(`/api/team-assignments/${ids[0]}/`, { is_active: newVal });
        }
        row.is_active = newVal;
      } catch (err) {
        console.error('Error toggling assignment:', err);
        // Surface it. This used to fail silently to the console, so a failed
        // write and an unresponsive button looked identical to the user.
        this.toggleError = 'Could not save that change. Please try again.';
      } finally {
        const next = new Set(this.pendingRuleIds);
        for (const id of ids) next.delete(id);
        this.pendingRuleIds = next;
      }
    },
    memberAllActive(group) {
      return group.rules.every(r => r.is_active);
    },
    memberAnyActive(group) {
      return group.rules.some(r => r.is_active);
    },
    async toggleMemberActive(group) {
      // If any are active, deactivate all. If all inactive, activate all.
      const newVal = !this.memberAnyActive(group);
      const changing = group.rules.filter(r => r.is_active !== newVal);
      if (!changing.length) return;
      // ONE batch call — NOT a per-rule PATCH loop. A case manager can have
      // hundreds of rules; the old Promise.all over per-rule PATCHes fired
      // hundreds of concurrent requests, exhausting the browser connection pool
      // ("Network Error") and saturating workers (incident 2026-06-29).
      // Each collapsed line carries every underlying row id in ruleIds.
      const ids = changing.flatMap(r => r.ruleIds && r.ruleIds.length ? r.ruleIds : [r.id]);

      if (this.isMemberPending(group)) return;

      // A member can have hundreds of rules (595 on one Vytalize CM), so this
      // write is the slowest one on the page and the most likely to look dead.
      this.pendingMemberIds = new Set([...this.pendingMemberIds, group.member_id]);
      this.toggleError = null;
      try {
        await this.$axios.post('/api/team-assignments/bulk-set-active/', {
          ids,
          is_active: newVal,
        });
        for (const r of changing) r.is_active = newVal;
      } catch (err) {
        console.error('Error bulk-toggling member assignments:', err);
        this.toggleError = `Could not update ${group.member_name || 'this member'}'s assignments. Please try again.`;
      } finally {
        const next = new Set(this.pendingMemberIds);
        next.delete(group.member_id);
        this.pendingMemberIds = next;
      }
    },
    async removeAssignment(rule) {
      // `rule` is a collapsed By-Member line; remove every underlying row it
      // represents (all its per-contract rows), not just the representative id.
      const ids = (rule && rule.ruleIds && rule.ruleIds.length)
        ? rule.ruleIds
        : [(rule && rule.id != null) ? rule.id : rule];
      if (!confirm('Are you sure you want to remove this assignment rule?')) return;
      try {
        await Promise.all(ids.map(id => this.$axios.delete(`/api/team-assignments/${id}/`)));
        const idSet = new Set(ids);
        this.assignments = this.assignments.filter(a => !idSet.has(a.id));
      } catch (err) {
        console.error('Error removing assignment:', err);
      }
    },

    async loadDropdownOptions() {
      if (this.optionsLoaded) return;
      try {
        const requests = [
          this.$axios.get('/api/entity-types/'),
          this.$axios.get('/api/team-assignments/roles/'),
        ];
        const [typesRes, rolesRes] = await Promise.all(requests);
        this.entityTypes = (typesRes.data.results || typesRes.data)
          .sort((a, b) => a.type_name.localeCompare(b.type_name));
        // Selected-entity names come from the EntitySearch widget (selectedEntityNames),
        // so no tenant-specific practice prefetch is needed. (Removed the hardcoded
        // Vytalize entity_type_id=11 fetch that left IHCI's picker empty.)
        this.roles = rolesRes.data || [];
        this._fetchedTaskTypes = [];

        // Contracts for the (optional) contract-scoped rule. A rule scoped to a
        // Contract entity outranks the practice/program rules in resolution
        // (assignment_service._get_candidate_pool). Resolved by the tenant's
        // "Contract" entity type rather than a hardcoded id, so it works for any
        // tenant that has one (e.g. IHCI 267, Vytalize 24).
        const contractType = this.entityTypes.find(t => t.type_name === 'Contract' || t.type_name === 'Contract Pool');
        if (contractType) {
          try {
            const { data } = await this.$axios.get(`/api/entities/?entity_type_id=${contractType.id}&page_size=1000`);
            this.contracts = (data.results || data || [])
              .map(e => ({ id: e.id, name: e.name }))
              .sort((a, b) => a.name.localeCompare(b.name));
          } catch (e) {
            console.error('Error loading contracts:', e);
          }
        }
        this.optionsLoaded = true;

        // Auto-select entity type if only one
        if (this.memberEntityTypes.length === 1) {
          const et = this.entityTypes.find(t => this.memberEntityTypes.includes(t.type_name));
          if (et) {
            this.assignEntityType = [et.id];
            await this.loadMemberEntities([et.id]);
          }
        }
      } catch (err) {
        console.error('Error loading options:', err);
      }
    },

    onPracticesSelected(entityIds, names) {
      this.assignEntities = entityIds;
      if (names) {
        this.selectedEntityNames = { ...this.selectedEntityNames, ...names };
      }
    },

    async onEntityTypeChange() {
      this.assignMembers = [];
      this.memberEntitiesLoaded = [];
      if (!this.assignEntityType.length) return;
      await this.loadMemberEntities(this.assignEntityType);
    },

    async loadMemberEntities(typeIds) {
      if (!Array.isArray(typeIds)) typeIds = [typeIds];
      this.membersLoading = true;
      try {
        const all = [];
        for (const typeId of typeIds) {
          const { data } = await this.$axios.get(`/api/entities/?entity_type_id=${typeId}&page_size=1000`);
          all.push(...(data.results || data));
        }
        // Deduplicate by id
        const seen = new Set();
        this.memberEntitiesLoaded = all
          .filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true; })
          .map(e => ({ id: e.id, name: e.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (err) {
        console.error('Error loading members:', err);
      } finally {
        this.membersLoading = false;
      }
    },

    closeModal() {
      this.showAssignModal = false;
      this.assignResult = null;
    },

    async submitAssign() {
      this.assignLoading = true;
      this.assignResult = null;

      const metricIds = this.assignPrograms.length
        ? this.assignPrograms.map(v => this.metricMap[v]).filter(Boolean)
        : this.allMetricIds;

      // Resolve task_type_ids: user-selected > fixedTaskTypeIds > undefined
      const taskTypeIds = this.assignTaskTypes.length
        ? this.assignTaskTypes
        : (this.fixedTaskTypeIds.length ? this.fixedTaskTypeIds : undefined);

      let totalAssigned = 0;
      let errors = [];

      for (const memberId of this.assignMembers) {
        const payload = {
          member_id: memberId,
          metric_ids: metricIds.length ? metricIds : undefined,
          role_id: this.assignRole.length ? this.assignRole[0] : undefined,
          task_type_ids: taskTypeIds,
        };
        if (this.assignEntities.length) {
          payload.entity_ids = this.assignEntities;
        }
        if (this.assignContracts.length) {
          payload.contract_ids = this.assignContracts;
        }

        try {
          const { data } = await this.$axios.post('/api/team-assignments/bulk-assign/', payload);
          totalAssigned += data.assigned || 0;
        } catch (err) {
          errors.push(this.memberName(memberId) + ': ' + (err.response?.data?.error || 'failed'));
        }
      }

      if (errors.length) {
        this.assignResult = { error: errors.join('; ') };
      } else {
        this.assignResult = { assigned: totalAssigned };
      }

      await this.loadAssignments();
      this.assignLoading = false;
    },

  },

  watch: {
    showAssignModal(val) {
      if (val) this.loadDropdownOptions();
    },
  },

  created() {
    this._fetchedTaskTypes = [];
  },

  mounted() {
    this.loadAssignments();
  },
};
</script>
